import { useState, useMemo } from 'react'
import { Chip, EmptyState, theme, Icon } from '@ketch-com/deck'
import { Box, Stack, Typography } from '@mui/material'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts'
import { AdditionalJurisdictionsModal } from '../historical/AdditionalJurisdictionsModal'

export interface PieChartDataItem {
  name: string
  value: number
  color?: string
  originalCount: number
  rights?: {
    rightCode: string
    rightName: string
    total: number
  }[]
}

interface DataPieChartProps {
  data: PieChartDataItem[]
  title?: string
  noDataMessage?: string
  noDataSubMessage?: string
  height?: number
  width?: string | number
  innerRadius?: number
  outerRadius?: number
  paddingAngle?: number
  showFallbackColors?: boolean
}

const CategoryTooltip = ({ payload }: TooltipProps<number, string>) => {
  const data = payload?.[0]?.payload as PieChartDataItem | undefined

  return data ? (
    <Box
      sx={{
        background: theme.palette.Text.Primary,
        borderRadius: theme.spacing(0.75),
        p: theme.spacing(1.25),
        minWidth: theme.spacing(31.25),
        display: 'flex',
        flexDirection: 'column',
        transform: `translateY(${(data.rights?.length ?? 0) < 10 ? -100 : -200}px)`,
      }}
    >
      <Typography variant="smallLabel" color={theme.palette.Common.Container} sx={{ mb: theme.spacing(0.75) }}>
        {data.name}
      </Typography>
      {Array.isArray(data.rights) && data.rights.length > 0 && (
        <Stack spacing={1} mt={1}>
          {data.rights.map(right => (
            <Typography key={right.rightCode} variant="smallBody" color={theme.palette.Common.Container}>
              {right.rightName ? right.rightName : right.rightCode}:{' '}
              <Typography variant="smallLabel">{right.total.toLocaleString()}</Typography>
            </Typography>
          ))}
        </Stack>
      )}
    </Box>
  ) : null
}

export const DataPieChart = ({
  data,
  height = 190,
  innerRadius = 44,
  outerRadius = 85,
  paddingAngle = 0,
  showFallbackColors = false,
}: DataPieChartProps) => {
  const [isAdditionalJurisdictionsOpen, setIsAdditionalJurisdictionsOpen] = useState(false)

  const maxLegendItems = 4

  const primaryColors = [
    theme.palette.Infographics.BlueA.Primary,
    theme.palette.Infographics.BlueE.Primary,
    theme.palette.Infographics.GreenA.Secondary,
    theme.palette.Infographics.YellowB.Secondary,
  ]

  const fallbackColors = [
    theme.palette.Red.Dark.o100,
    theme.palette.Error.PrimaryLight,
    theme.palette.Warning.Primary,
    theme.palette.Action.Primary,
  ]

  const COLORS = showFallbackColors ? fallbackColors : primaryColors

  const { topItems, othersItem } = useMemo(() => {
    if (!data || data.length <= maxLegendItems) {
      return {
        topItems: data.map((item, index) => ({
          ...item,
          color: COLORS[index % COLORS.length],
        })),
        othersItem: null,
      }
    }

    const sortedData = data.toSorted((a, b) => b.value - a.value)
    const top = sortedData.slice(0, maxLegendItems).map((item, index) => ({
      ...item,
      color: COLORS[index % COLORS.length],
    }))
    const others = sortedData.slice(maxLegendItems)

    const { othersValue, othersOriginalCount } = others.reduce(
      (acc, item) => {
        acc.othersValue += item.value
        acc.othersOriginalCount += item.originalCount
        return acc
      },
      { othersValue: 0, othersOriginalCount: 0 },
    )

    const othersItem: PieChartDataItem = {
      name: 'Others',
      value: othersValue,
      originalCount: othersOriginalCount,
    }

    return {
      topItems: top,
      othersItem,
    }
  }, [data, COLORS])

  if (!data?.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height,
          textAlign: 'center',
          marginTop: '48px',
        }}
      >
        <EmptyState
          size="small"
          colorTheme={theme.palette.Black.o48}
          iconName="FPie"
          title="No Data"
          subTitle="Adjust your search criteria or filters and try again."
        />
      </Box>
    )
  }

  return (
    <Stack direction="row" alignItems="center">
      {/* Chart container */}
      <Box sx={{ width: 200, height: 200, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={topItems}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              paddingAngle={paddingAngle}
              fill="#8884d8"
              cx="50%"
              cy="50%"
              dataKey="value"
              isAnimationActive={false}
              label={false}
            >
              {topItems.map(entry => (
                <Cell key={`cell-${entry.name}`} fill={entry.color} />
              ))}
            </Pie>
            {topItems.length > 0 && <Tooltip content={<CategoryTooltip />} wrapperStyle={{ zIndex: 1300 }} />}
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Legend container */}
      <Stack spacing={1} marginLeft="40px">
        {topItems.map(entry => (
          <Stack direction="row" alignItems="center" key={entry.name}>
            <Typography variant="smallLabel" minWidth={50} textAlign="right">
              {entry.value.toLocaleString()}
            </Typography>
            <Box
              sx={{
                width: theme.spacing(0.75),
                height: theme.spacing(0.75),
                borderRadius: '50%',
                backgroundColor: entry.color,
                flexShrink: 0,
                m: 1,
              }}
            />
            <Typography variant="smallBody">{entry.name.charAt(0).toUpperCase() + entry.name.slice(1)}</Typography>
          </Stack>
        ))}

        {othersItem && (
          <Box sx={{ pl: '35px' }}>
            <Chip
              label={
                <>
                  <Typography variant="smallLabel" sx={{ mr: 0.5 }}>
                    {othersItem.value.toLocaleString()}
                  </Typography>
                  <Typography variant="smallBody">Other</Typography>
                </>
              }
              size="small"
              deleteIcon={<Icon name="OArrowCRight" iconColor={theme.palette.Action.PrimaryDark} />}
              onDelete={() => setIsAdditionalJurisdictionsOpen(true)}
              onClick={() => setIsAdditionalJurisdictionsOpen(true)}
              sx={{
                backgroundColor: theme.palette.Action.Disabled,
                color: theme.palette.Action.PrimaryDark,
              }}
            />
          </Box>
        )}
        {isAdditionalJurisdictionsOpen && (
          <AdditionalJurisdictionsModal data={data} onClose={() => setIsAdditionalJurisdictionsOpen(false)} />
        )}
      </Stack>
    </Stack>
  )
}
